import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { Address, AddressProducts, ClearingAccount, ContractStatusEnum, IVerknuepfungenResponse, RecentOrder } from '@mwe/models';
import { computed, inject } from '@angular/core';
import { filterAddressGroupsOnContractState, isContractStatus } from '@mwe/utils';
import { SessionStorageService } from '@mwe/utils';

interface ProductStoreState {
  associations: IVerknuepfungenResponse;
  associationsOrig: IVerknuepfungenResponse;
  recentOrders: RecentOrder[];
  addressGroupsInternal: AddressProducts[];
  currentState: ContractStatusEnum;
  filterButtonOptions: ContractStatusEnum[];
  clearingAccounts: ClearingAccount[];
}

const newState = {
  associations: undefined,
  associationsOrig: undefined,
  recentOrders: undefined,
  currentState: ContractStatusEnum.Aktiv,
  addressGroupsInternal: [],
  filterButtonOptions: [ContractStatusEnum.Aktiv, ContractStatusEnum.Beendet],
  clearingAccounts: [],
} as ProductStoreState;

export const PRODUCTS_FILTER_STATE = 'products_filter_state';

export const productStore = signalStore(
  {
    providedIn: 'root',
  },
  withState<ProductStoreState>({ ...newState }),
  withComputed(store => {
    const addressGroups = computed(() =>
      filterAddressGroupsOnContractState({
        addressGroups: store.addressGroupsInternal(),
        contractState: store.currentState(),
      }),
    );

    const activeProducts = computed(() => {
      return addressGroups()
        .flatMap(addressGroup => addressGroup.products)
        .filter(product => isContractStatus(product.vertragsStatus, ContractStatusEnum.Aktiv));
    });

    const hasOnlyInactiveProducts = computed(() => {
      return store.addressGroupsInternal().every(addressGroup => {
        return addressGroup.products.every(product => isContractStatus(product.vertragsStatus, ContractStatusEnum.Beendet));
      });
    });

    const hasOnlyActiveProducts = computed(() => {
      return store.addressGroupsInternal().every(addressGroup => {
        return addressGroup.products.every(product => isContractStatus(product.vertragsStatus, ContractStatusEnum.Aktiv));
      });
    });

    const hasActiveAndInactiveProducts = computed(() => {
      const hasActiveProducts = store
        .addressGroupsInternal()
        .some(ag => ag.products.some(p => isContractStatus(p.vertragsStatus, ContractStatusEnum.Aktiv)));
      const hasInactiveProducts = store
        .addressGroupsInternal()
        .some(ag => ag.products.some(p => isContractStatus(p.vertragsStatus, ContractStatusEnum.Beendet)));
      return hasActiveProducts && hasInactiveProducts;
    });

    const hasInactiveProducts = computed(() => {
      return addressGroups().some(addressGroup => {
        return addressGroup.products.some(product => isContractStatus(product.vertragsStatus, ContractStatusEnum.Beendet));
      });
    });

    const products = computed(() => {
      return addressGroups().flatMap(addressGroup => addressGroup.products);
    });

    return {
      activeProducts,
      addressGroups,
      products,
      hasOnlyInactiveProducts,
      hasOnlyActiveProducts,
      hasActiveAndInactiveProducts,
      hasInactiveProducts,
    };
  }),
  withMethods(store => {
    const storageService = inject(SessionStorageService);
    const setAssociationsOrig = (associationsOrig: IVerknuepfungenResponse) => {
      patchState(store, state => ({ ...state, associationsOrig }));
    };
    const setAssociations = (associations: IVerknuepfungenResponse) => {
      patchState(store, state => ({ ...state, associations }));
    };
    const setRecentOrders = (recentOrders: RecentOrder[]) => {
      patchState(store, state => ({ ...state, recentOrders }));
    };
    const setAddressGroups = (addressGroupsInternal: AddressProducts[]) => {
      patchState(store, state => ({ ...state, addressGroupsInternal }));
    };
    const updateCurrentStatePersistent = () => {
      storageService.store(PRODUCTS_FILTER_STATE, store.currentState());
    };
    const setCurrentState = (currentState: ContractStatusEnum) => {
      patchState(store, state => ({ ...state, currentState }));
      updateCurrentStatePersistent();
    };
    const updateAddressGroup = (address: Address) => {
      patchState(store, state => {
        const addressGroupsInternal: AddressProducts[] = state.addressGroupsInternal.map((ag: AddressProducts) => {
          if (ag.address.hashCode !== address.hashCode) {
            return ag;
          }

          const products = ag.products.map(p => {
            return {
              ...p,
              address,
            };
          });

          return {
            ...ag,
            address,
            products,
          } as AddressProducts;
        });

        return {
          ...state,
          addressGroupsInternal,
        };
      });
    };

    const setClearingAccounts = (clearingAccounts: ClearingAccount[]) => {
      patchState(store, state => ({ ...state, clearingAccounts }));
    };

    const reset = () => {
      patchState(store, _ => ({ ...newState }));
    };

    return {
      setAssociations,
      setAssociationsOrig,
      setRecentOrders,
      setAddressGroups,
      setCurrentState,
      updateCurrentStatePersistent,
      updateAddressGroup,
      setClearingAccounts,
      reset,
    };
  }),
);
export type productStore = InstanceType<typeof productStore>;
